/* eslint-disable @typescript-eslint/no-unsafe-return */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Modal from '@saucelabs/ds-modal';
import { modalConnectV2 } from '@saucelabs/sl-container-modal';
import { Typography } from '@saucelabs/ds-typography';
import { InputTextArea, Select, Option } from '@saucelabs/ds-inputs';
import { STANDARD_EVENTS, trackStandardEvent } from '@saucelabs/sl-util-segmentio';

import {
  CANCEL_SUBSCRIPTION_SURVEY_MODAL,
  SURVEY_QUESTIONS,
} from '../../constants';
import { PRODUCT_AREA, PRODUCT_SUB_AREAS, SUBJECT } from '../../tracking';

export interface CancelSubscriptionSurveyModalProps {
  onConfirmCancelSubscription: () => Promise<void>;
  onHideCancelSubscription?: () => void;
  planType: string;
}

const CancelSubscriptionSurveyModal: React.FC<CancelSubscriptionSurveyModalProps> = ({
  onConfirmCancelSubscription,
  onHideCancelSubscription,
  planType,
}) => {
  // Sample Surveys Value: {
  //   [question1]: [answer1],
  //   [question2]: [answer2],
  // }
  const [surveysValue, setSurveysValue] = useState({});
  const values = Object.values(surveysValue).filter((val) => !!val);

  // Use a seperate <string> state for the text field to prevent mismatch answers
  const [surveysTextValue, setSurveysTextValue] = useState < string | undefined >();

  const isDisabled = !(values.length >= SURVEY_QUESTIONS.length);

  return (
    <Modal
      actionButtonProps={{
        disabled: isDisabled,
        children: 'Cancel my Subscription',
        type: 'submit',
      }}
      emphasis="critical"
      hideButtonProps={{
        children: 'Keep my Subscription',
      }}
      onConfirm={async () => {
        trackStandardEvent(
          STANDARD_EVENTS.OBJECT_CLICKED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CANCELLATION,
          SUBJECT.CONFIRM_CANCELLATION,
          {
            reason: values[0],
            competitor: values[1],
            other: surveysTextValue, // it can be undefined
            planType,
          },
        );
        await onConfirmCancelSubscription();
      }}
      onHide={() => {
        trackStandardEvent(
          STANDARD_EVENTS.OBJECT_CLICKED,
          PRODUCT_AREA,
          PRODUCT_SUB_AREAS.CANCELLATION,
          SUBJECT.HIDE_SURVEY_MODAL,
        );
        onHideCancelSubscription();
      }}
      title="Help us improve"
    >
      <Typography
        disableSpacing
        variant="body-small"
      >
        Please provide feedback to help us improve our products.
      </Typography>
      <div className="text-base mb-1">
        { SURVEY_QUESTIONS.map((survey) => {
          const { key, question, answerList } = survey;
          return (
            <div
              key={key}
              className="mt-3 mb-3"
            >
              <div className="p-0.25">
                <Select
                  defaultSelectedKey={surveysValue[question]}
                  label={question}
                  onSelectionChange={(anwser) => {
                    setSurveysValue({
                      ...surveysValue,
                      [question]: anwser,
                    });
                  }}
                >
                  {answerList.map((opt) => (
                    <Option key={opt.value}>{opt.label}</Option>
                  ))}
                </Select>
              </div>
            </div>
          );
        })}
        <InputTextArea
          className="mt-1"
          label="Additional feedback"
          maxLength={undefined}
          onChange={(content) => {
            const response = (content.trim() === '') ? undefined : content;
            setSurveysTextValue(response);
          }}
          optional
        />
      </div>
    </Modal>
  );
};

CancelSubscriptionSurveyModal.propTypes = {
  onConfirmCancelSubscription: PropTypes.func.isRequired,
  onHideCancelSubscription: PropTypes.func,
};

CancelSubscriptionSurveyModal.defaultProps = {
  onHideCancelSubscription: () => undefined,
};

export default modalConnectV2(CANCEL_SUBSCRIPTION_SURVEY_MODAL, CancelSubscriptionSurveyModal);
export { CancelSubscriptionSurveyModal };
